
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import Icons from '@/components/Icons';
import { useNavigate } from 'react-router-dom';

const ChangePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    number: false,
    special: false
  });
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Check password strength
    setPasswordStrength({
      length: password.length >= 8,
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password match
    if (password !== confirmPassword) {
      toast({
        title: "Fehler",
        description: "Passwörter stimmen nicht überein.",
        variant: "destructive",
      });
      return;
    }

    // Validate password strength
    if (!passwordStrength.length || !passwordStrength.number || !passwordStrength.special) {
      toast({
        title: "Passwort zu schwach",
        description: "Dein Passwort muss mindestens 8 Zeichen lang sein und mindestens eine Zahl sowie ein Sonderzeichen enthalten.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ 
        password 
      });

      if (error) {
        throw error;
      }

      setIsComplete(true);
      toast({
        title: "Erfolg",
        description: "Dein Passwort wurde erfolgreich geändert.",
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-chrona-background">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Link to="/">
            <img alt="CHRONA" src="/lovable-uploads/dfc64acd-e312-44ad-9a99-c99e727300e4.png" className="h-28 w-auto" />
          </Link>
        </div>
        
        <Card className="shadow-xl p-6 md:p-8 bg-chrona-gray-800 border-chrona-gray-700">
          <div className="mb-6">
            <Link to="/login" className="text-chrona-gray-300 hover:text-chrona-text flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" /> Zurück zum Login
            </Link>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-6 text-center">
            {isComplete ? "Passwort erfolgreich geändert" : "Neues Passwort erstellen"}
          </h1>
          
          {isComplete ? (
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <Icons.check className="h-8 w-8 text-green-600" />
                </div>
              </div>
              
              <p className="mb-4 text-chrona-gray-300">
                Dein Passwort wurde erfolgreich geändert. Du wirst in wenigen Sekunden zum Login weitergeleitet.
              </p>
              
              <Button
                className="w-full bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text"
                onClick={() => navigate('/login')}
              >
                Zum Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-chrona-gray-300">Neues Passwort</Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text"
                />
              </div>
              
              {/* Password strength indicator */}
              <div className="space-y-2">
                <p className="text-sm text-chrona-gray-300">Passwort muss enthalten:</p>
                <ul className="space-y-1">
                  <li className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-500' : 'text-chrona-gray-400'}`}>
                    {passwordStrength.length ? <Icons.check className="mr-1 h-3 w-3" /> : <Icons.circle className="mr-1 h-3 w-3" />}
                    Mindestens 8 Zeichen
                  </li>
                  <li className={`text-xs flex items-center ${passwordStrength.number ? 'text-green-500' : 'text-chrona-gray-400'}`}>
                    {passwordStrength.number ? <Icons.check className="mr-1 h-3 w-3" /> : <Icons.circle className="mr-1 h-3 w-3" />}
                    Mindestens eine Zahl
                  </li>
                  <li className={`text-xs flex items-center ${passwordStrength.special ? 'text-green-500' : 'text-chrona-gray-400'}`}>
                    {passwordStrength.special ? <Icons.check className="mr-1 h-3 w-3" /> : <Icons.circle className="mr-1 h-3 w-3" />}
                    Mindestens ein Sonderzeichen
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-chrona-gray-300">Passwort bestätigen</Label>
                <PasswordInput
                  id="confirm-password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                    Speichere...
                  </>
                ) : (
                  'Passwort ändern'
                )}
              </Button>
            </form>
          )}
        </Card>

        <div className="mt-8 text-center">
          <p className="text-chrona-gray-300 text-sm">
            Die einfachste Art, <span className="text-[#2C6E49]">Termine zu buchen</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
